import type { SvgIconComponent } from '@mui/icons-material';
import { Box, Button, DialogActions, DialogContent, Slider } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import type { DeviceBulkSelectionInput, DeviceCommandKind } from '~/generated/graphql';

interface DeviceSliderDialogProps {
  onClose: () => void;
  onComplete?: () => void;
  open: boolean;
  selected: DeviceBulkSelectionInput;
  command: DeviceCommandKind;
  defaultValue: number;
  Icon: SvgIconComponent;
  title: string;
  buttonText: string;
  LeftIcon?: SvgIconComponent;
  RightIcon?: SvgIconComponent;
}

export const DeviceSliderDialog = ({
  onClose,
  onComplete,
  open,
  selected,
  command,
  defaultValue,
  Icon,
  title,
  buttonText,
  LeftIcon,
  RightIcon,
}: DeviceSliderDialogProps) => {
  const { t } = useTranslation();
  const [level, setLevel] = useState<number>(defaultValue);
  const [sendCommand, { loading }] = useSendDeviceCommand();

  const onSubmit = () => {
    void sendCommand({
      variables: {
        input: { command, payload: { level }, selected },
      },
    });
    onClose();
    onComplete?.();
  };

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>

      <DialogContent>
        <Box sx={{ px: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {LeftIcon && <LeftIcon sx={{ color: 'text.secondary' }} />}
            <Slider
              value={level}
              onChange={(_, value) => setLevel(value as number)}
              min={0}
              max={100}
              step={1}
              valueLabelDisplay="on"
              aria-label={title}
              sx={{ flex: 1 }}
            />
            {RightIcon && <RightIcon sx={{ color: 'text.secondary' }} />}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={loading} onClick={onClose} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={loading}
          onClick={onSubmit}
          startIcon={<Icon />}
          variant="contained"
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
